import type { RouteView } from "../contract"
import { create } from "zustand"
import { track } from "@/lib/analytics"
import { useTurnStore } from "../store"
import type { TurnRequest } from "../contract"
import type { ThreadContext } from "../readerContext"

/** Why a turn was sent: a fresh question, clarify answers, or edited matter context. */
export type TurnKind = "ask" | "answers" | "context"

/** Local facts about a turn that the server stream does not carry. */
export interface TurnMeta {
  request: TurnRequest
  kind: TurnKind
  /** Short "You told NIRA" line for answers and context turns. */
  summary?: string
  /** True when the turn was started or followed live in this tab (drives the loader hold). */
  live: boolean
  /** Set once Retry re-sent this turn, so the button does not offer it twice. */
  retried?: boolean
  /** The X-Request-Id of the POST that started this turn (known only for turns sent from this tab). */
  requestId?: string
  /** The newer turn that re-ran this one (edited matter, retry, re-run). A superseded turn shows a one-line stub. */
  supersededBy?: string
  /** Who this turn was answered for, as the server settled it at turn.started. */
  readerAt?: ThreadContext
}

export interface OpenSection { turn: string; index: number }
export interface OpenSource {
  turn: string
  id: string
  /** Quote that opened the panel; used when the source carries no relied phrase of its own. */
  relied?: { before: string; phrase: string; after: string } | null
  from: HTMLElement | null
}

interface AnswerSession {
  meta: Record<string, TurnMeta>
  /** First envelope time seen per step id, per turn (the reducer keeps steps without `t`). */
  stepT: Record<string, Record<string, number>>
  /** ask_back choices keyed `${turn}:${section}:${block}`. */
  choices: Record<string, string>
  /** checklist ticks keyed `${turn}:${section}:${block}`. */
  checks: Record<string, boolean[]>
  /** Clarify turns that were answered, with the summary line. */
  resolved: Record<string, string>
  open: OpenSection | null
  source: OpenSource | null
  notice: string | null
  /** The last question was a notice or an order: its own door (nira/agent/route.py). */
  route: (RouteView & { question?: string }) | null
  setRoute: (route: (RouteView & { question?: string }) | null) => void
  /** The thread's name as the server holds it (the matter title once an answer names it). */
  title: string
  setTitle: (title: string) => void
  /** The case file this thread is in, once a notice started one. */
  caseFile: { id: string; title: string } | null
  setCaseFile: (c: { id: string; title: string } | null) => void
  setMeta: (key: string, meta: TurnMeta) => void
  patchMeta: (key: string, patch: Partial<TurnMeta>) => void
  noteStep: (key: string, id: string, t: number) => void
  choose: (k: string, value: string) => void
  setChecks: (k: string, value: boolean[]) => void
  resolve: (key: string, summary: string) => void
  openSection: (open: OpenSection | null) => void
  openSource: (source: OpenSource | null) => void
  setNotice: (notice: string | null) => void
  reset: () => void
}

const initial = {
  meta: {}, stepT: {}, choices: {}, checks: {}, resolved: {}, open: null, source: null, notice: null, route: null, title: "", caseFile: null,
}

/** Page-local UI state for the answer page (not persisted; the server owns the turns). */
export const useAnswerSession = create<AnswerSession>((set) => ({
  ...initial,
  setMeta: (key, meta) => set((s) => ({ meta: { ...s.meta, [key]: meta } })),
  patchMeta: (key, patch) =>
    set((s) => (s.meta[key] ? { meta: { ...s.meta, [key]: { ...s.meta[key], ...patch } } } : s)),
  noteStep: (key, id, t) =>
    set((s) => {
      const cur = s.stepT[key] ?? {}
      if (id in cur) return s
      return { stepT: { ...s.stepT, [key]: { ...cur, [id]: t } } }
    }),
  choose: (k, value) => set((s) => ({ choices: { ...s.choices, [k]: value } })),
  setChecks: (k, value) => set((s) => ({ checks: { ...s.checks, [k]: value } })),
  resolve: (key, summary) => set((s) => ({ resolved: { ...s.resolved, [key]: summary } })),
  openSection: (open) => set({ open }),
  openSource: (source) => {
    // A case or a provision opened (the reader's own document is not a citation).
    const kind = source ? useTurnStore.getState().turns[source.turn]?.sources[source.id]?.kind : undefined
    if (kind === "case" || kind === "law") track("citation_opened", { kind })
    set({ source })
  },
  setNotice: (notice) => set({ notice }),
  setRoute: (route) => set({ route }),
  setTitle: (title) => set({ title }),
  setCaseFile: (caseFile) => set({ caseFile }),
  reset: () => set(initial),
}))

export const blockKey = (turn: string, section: string, block: number | string) => `${turn}:${section}:${block}`
